import { watch } from 'vue';
import { createI18n } from 'vue-i18n';
import { CURRENCY_DEFAULT } from '@/app/data/currencies_constants';

// Deep merge utility for i18n HMR
function deepMerge(target, source) {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key] || typeof target[key] !== 'object') {
        target[key] = {};
      }
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

function loadLocaleMessages() {
  const locales = import.meta.glob('./locales/*.*.json', { eager: true, import: 'default' });
  const messages = {};
  Object.keys(locales).forEach((key) => {
    const matched = key.match(/\.([a-z]+)/);
    if (matched && matched.length > 1) {
      const locale = matched[1];

      if (!messages[locale]) {
        messages[locale] = {};
      }

      messages[locale] = { ...messages[locale], ...locales[key][locale] };
    }
  });
  return messages;
}

const DEFAULT_CURRENCY = {
  style: 'currency',
  currency: CURRENCY_DEFAULT,
  maximumFractionDigits: 2,
};

const numberFormats = {
  en: { currency: DEFAULT_CURRENCY },
  fr: { currency: DEFAULT_CURRENCY },
  nl: { currency: DEFAULT_CURRENCY },
  pt: { currency: DEFAULT_CURRENCY },
  es: { currency: DEFAULT_CURRENCY },
  it: { currency: DEFAULT_CURRENCY },
  el: { currency: DEFAULT_CURRENCY },
};

function pluralRule(choice, choicesLength, orgRule) {
  // choicesLength is 4
  // this is reserved for notifications, combination of a single/multiple notifier(s) + single/multiple notifiable(s)
  // choice 0: single notifier, single notifiable
  // choice 1: single notifier, multiple notifiables
  // choice 2: multiple notifiers, single notifiable
  // choice 3: multiple notifiers, multiple notifiables
  if (choicesLength === 4) {
    return choice;
  }
  // choicesLength is 3: return default function
  return orgRule(choice, choicesLength);
}

const LOCALE_FALLBACK = 'en';

function normalizeLocale(locale) {
  if (!locale) return LOCALE_FALLBACK;
  return String(locale).toLowerCase().split(/[-_]/)[0] || LOCALE_FALLBACK;
}

function getBrowserLocale(locale_list_supported) {
  if (typeof navigator === 'undefined' || !navigator.language) return LOCALE_FALLBACK;

  const locale_normalized = normalizeLocale(navigator.language);
  if (Array.isArray(locale_list_supported) && locale_list_supported.includes(locale_normalized)) {
    return locale_normalized;
  }

  return LOCALE_FALLBACK;
}

function setDocumentLang(locale) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', normalizeLocale(locale));
}

const messages = loadLocaleMessages();
const locale_list_supported = Object.keys(messages);
const locale_initial = getBrowserLocale(locale_list_supported);

const i18n = createI18n({
  locale: locale_initial,
  legacy: false,
  fallbackLocale: 'en',
  messages,
  numberFormats,
  warnHtmlInMessage: false,
  warnHtmlMessage: false,
  pluralRules: {
    en: pluralRule,
    nl: pluralRule,
    fr: pluralRule,
    pt: pluralRule,
    es: pluralRule,
    it: pluralRule,
    el: pluralRule,
  },
});

// Keep the document language in sync with the active locale.
// Note: index.html includes a static lang attribute for accessibility scanners;
// this watcher ensures it stays correct once the app loads and when users change language.
watch(
  () => i18n.global.locale.value,
  (locale_new) => {
    setDocumentLang(locale_new);
  },
  { immediate: true },
);

// Vite HMR: Listen for i18n-update and reload messages
if (import.meta.hot) {
  import.meta.hot.on('i18n-update', async (payload) => {
    // Only reload the changed file
    const file = payload.file;
    if (file) {
      // Dynamic import to reload the changed locale file
      try {
        const match = file.match(/([a-zA-Z-]+)\.([a-z]+)\.json$/);
        if (match) {
          const locale = match[2];
          const mod = await import(/* @vite-ignore */ `${file}?t=${Date.now()}`);
          // Deep merge new translations into existing locale messages
          const newMessages = mod.default[locale] || mod.default;
          const currentMessages = i18n.global.getLocaleMessage(locale) || {};
          i18n.global.setLocaleMessage(locale, deepMerge({ ...currentMessages }, newMessages));
        }
      } catch (e) {
        // oxlint-disable-next-line no-console
        console.warn('Failed to reload i18n file:', file, e);
      }
    }
  });
}

export default i18n;
